import React, { createContext, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { canAddPersonalData } from '@fs/zion-permissions'
import getAuthorizedPermissions from '../../services/permissionService'
import { getUserPreferredRole, setUserPreferredRole } from '../../services/userPreferencesService'
import { getSessionUserId } from '../../services/userService'
import { enterRole, exitRole, getActiveRole } from '../../util/Roles'
import useNotifications from '../../hooks/useNotifications'

export const PermissionsContext = createContext()

export function PermissionsProvider({ children }) {
  const [authorizedPermissions, setAuthorizedPermissions] = useState()
  const [currentRole, setCurrentRole] = useState('')
  const [currentPreferredRole, setCurrentPreferredRole] = useState(undefined)
  const [isRussian, setIsRussian] = useState(false)
  const { error } = useNotifications()
  const { t } = useTranslation()

  const checkRussianAccess = async (role, userId) => {
    const activeRole = await getActiveRole(true)
    if (
      (isRussian && !role.toLowerCase().includes('russian')) ||
      (!isRussian && role.toLowerCase().includes('russian'))
    ) {
      error(`You can ${isRussian ? 'only' : 'not'} use Russian Roles`)
      exitRole()
    } else if (activeRole !== role) {
      await setUserPreferredRole(role, userId)
      await enterRole(role)
      setCurrentPreferredRole(role)
    }
  }
  const autoElevateUserToPreferredRole = async () => {
    const activeRole = await getActiveRole(true)
    // DATA-12253: return the full promise chain so callers that `await` this function wait for
    // role elevation (enterRole, via checkRussianAccess) to finish. Without these `return`s the
    // async function resolved after getActiveRole() only, letting the caller's
    // getAuthorizedPermissions(true) race ahead of enterRole() and fetch permissions/new against
    // the pre-elevation session — a transient source of the DATA-DMCONSOLE-E 403.
    return getSessionUserId()
      .then((sessionUserIdResponse) => {
        const userId = sessionUserIdResponse.data.userId
        if (userId) {
          return getUserPreferredRole(userId)
            .then(async (userPreferredRoleResponse) => {
              const userPreferredRole = userPreferredRoleResponse.data.value
              if (activeRole) {
                setCurrentRole(activeRole)
                if (activeRole !== userPreferredRole) {
                  await setUserPreferredRole(activeRole, userId)
                }
              } else if (userPreferredRole) {
                return checkRussianAccess(userPreferredRole, userId)
              }
              return undefined
            })
            .catch(() => {
              window.location.replace(`/auth/logout?returnUrl=${encodeURIComponent(window.location)}`)
            })
        }
        window.location.replace(`/auth/login?returnUrl=${encodeURIComponent(window.location)}`)
        return undefined
      })
      .catch(() => {
        window.location.replace(`/auth/login?returnUrl=${encodeURIComponent(window.location)}`)
      })
  }

  useEffect(() => {
    async function initializePermissions() {
      const russianStatus = !(await canAddPersonalData())
      setIsRussian(russianStatus)
      await autoElevateUserToPreferredRole()
      try {
        const response = await getAuthorizedPermissions(true)
        setAuthorizedPermissions(response)
      } catch (err) {
        error(t('permissions.errorFetchingPermissions', { defaultValue: 'Unable to load your permissions' }), err)
        setAuthorizedPermissions([])
      }
    }
    initializePermissions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, t])

  const value = useMemo(
    () => ({
      isAuthorized: (requiredPermissions) => isAuthorized(requiredPermissions, authorizedPermissions),
      isRole: (requiredRoles) => isRole(requiredRoles, currentRole),
      isPreferredRole: (preferredRoles) => isPreferredRole(preferredRoles, currentPreferredRole),
      permissionsLoading: authorizedPermissions === undefined,
    }),
    [authorizedPermissions, currentRole, currentPreferredRole]
  )
  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
}

// This function is exported for tests. Don't use it directly.
// You need "const { isAuthorized } = useContext(PermissionsContext)"
export function isAuthorized(requiredPermissions, authorizedPermissions) {
  if (authorizedPermissions && requiredPermissions) {
    if (Array.isArray(requiredPermissions)) {
      return requiredPermissions.every((perm) => authorizedPermissions.includes(perm))
    }
    return authorizedPermissions.includes(requiredPermissions)
  }
  return false
}

// This function is exported for tests. Don't use it directly.
// You need "const { isRole } = useContext(PermissionsContext)"
export function isRole(requiredRoles, currentRole) {
  if (Array.isArray(requiredRoles)) {
    return requiredRoles.some((requiredRole) => currentRole === requiredRole)
  }
  return currentRole === requiredRoles
}

// This function is exported for tests. Don't use it directly.
// You need "const { isPreferredRole } = useContext(PermissionsContext)"
export function isPreferredRole(preferredRoles, currentPreferredRole) {
  if (Array.isArray(preferredRoles)) {
    return preferredRoles.some((preferredRole) => preferredRole === currentPreferredRole)
  }
  return preferredRoles === currentPreferredRole
}
